import { cn } from "@/lib/utils";
import {
  ReactFramework,
  TemplateStack,
  StackMintlifyLinkMapping,
} from "@/utils/templateProjects";
import { ArrowLeft, Copy } from "lucide-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import useSupabaseConnectStore from "@/store/zustand/supabaseConnectStore";
import Stripe from "stripe";
import { Polar } from "@polar-sh/sdk";
import { Button } from "@/components/ui/button";
import SupabaseConnect from "../LeftNav/SupabaseConnect";
export type FormValues = {
  clerk: {
    publishableKey: string;
    secretKey: string;
  };
  convex: {
    deploymentUrl: string;
    deploymentKey: string;
  };
  polar: {
    accessToken: string;
    organizationId: string;
    webhookSecret: string;
    verifyWebhookFields: string;
  };
  stripe: {
    secretKey: string;
    webhookSecret: string;
    publicKey: string;
    verifyWebhookFields: string;
  };
};

// ===== Type Definitions =====
interface ProviderSetupProps {
  templateStack: TemplateStack;
  reactFramework: ReactFramework;
  onBack: () => void;
  onComplete: () => void;
  saveActionText: string;
  formValues: FormValues;
  onFormValuesChange?: (formValues: FormValues) => void;
}

// Integration form component for reuse
const IntegrationForm: React.FC<{
  title: string;
  icon: string;
  description: string;
  docsUrl: string;
  fields: Array<{
    label: string;
    placeholder: string;
    type?: string;
    onChange?: (value: string) => void;
    validationFunction?: (value: string) => boolean;
    validationErrorMessage?: string | undefined;
    checkboxLabel?: React.ReactNode;
    value: string;
  }>;
  isInfoOnly?: boolean;
  infoMessage?: string | undefined;
  footer?: React.ReactNode;
  selectedTemplateStack: TemplateStack;
  reactFramework: ReactFramework;
}> = ({
  title,
  icon,
  description,
  docsUrl,
  fields,
  isInfoOnly,
  infoMessage,
  footer,
  selectedTemplateStack,
  reactFramework,
}) => {
  const mintlifyURL =
    StackMintlifyLinkMapping?.[reactFramework]?.[selectedTemplateStack];

  return (
    <div className="border border-[#2A2A2D] p-4 rounded-lg mb-4 relative">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <img src={icon} alt={title} className="w-5 h-5" />
          <h3 className="text-base font-medium">{title}</h3>
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="text-xs bg-secondary text-white border border-[#2A2A2D]"
          onClick={() => window.open(mintlifyURL || docsUrl, "_blank")}
        >
          Learn More
        </Button>
      </div>
      <p className="text-gray-400 mb-3 text-sm">{description}</p>

      {isInfoOnly ? (
        <div className="bg-[#1F1F22] p-3 rounded-md border border-[#2A2A2D]">
          <p className="text-sm text-gray-300">{infoMessage}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {fields.map((field, index) => (
            <div key={index}>
              <label className="block text-xs font-medium mb-1">
                {field.label}
              </label>
              {field.validationFunction &&
                field?.value &&
                !field.validationFunction(field?.value) && (
                  <p className="text-red-500 text-sm">
                    {field.validationErrorMessage}
                  </p>
                )}

              {field.type !== "checkbox" ? (
                <input
                  type={field.type || "text"}
                  placeholder={field.placeholder}
                  className="w-full bg-[#2A2A2D] border border-gray-700 rounded-md p-1.5 text-sm text-white"
                  value={field?.value || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange?.(value);
                  }}
                />
              ) : (
                <div className="flex w-full items-center justify-start gap-2">
                  <input
                    type="checkbox"
                    placeholder={field.placeholder}
                    className="bg-[#2A2A2D] border border-gray-700 rounded-md p-1.5 text-sm text-white cursor-pointer"
                    checked={field?.value === "true"}
                    onChange={(e) => {
                      const value = e.target.checked;
                      field.onChange?.(value ? "true" : "false");
                    }}
                  />
                  {field?.checkboxLabel ? (
                    field?.checkboxLabel
                  ) : (
                    <p className="text-sm text-gray-400">{field?.label}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {footer}
    </div>
  );
};

// ===== Main Component =====
export const ProviderSetup: React.FC<ProviderSetupProps> = ({
  templateStack,
  reactFramework,
  onBack,
  onComplete,
  saveActionText,
  formValues,
  onFormValuesChange,
}) => {
  const connectedProjects = useSupabaseConnectStore(
    (state) => state.connectedProjects,
  );

  const connectedProjectId = useSupabaseConnectStore(
    (state) => state.connectedProjectId,
  );

  // Stack compatibility helpers
  const isClerkConvexStack = (stack: TemplateStack) => {
    return (
      stack === TemplateStack.CLERK_CONVEX_STRIPE ||
      stack === TemplateStack.CLERK_CONVEX_POLAR ||
      stack === TemplateStack.CLERK_CONVEX
    );
  };

  const isSupabaseStack = (stack: TemplateStack) => {
    return (
      stack === TemplateStack.SUPABASE_STRIPE ||
      stack === TemplateStack.SUPABASE_POLAR ||
      stack === TemplateStack.SUPABASE
    );
  };

  const hasStripePayment = (stack: TemplateStack) => {
    return (
      stack === TemplateStack.SUPABASE_STRIPE ||
      stack === TemplateStack.CLERK_CONVEX_STRIPE
    );
  };

  const hasPolarPayment = (stack: TemplateStack) => {
    return (
      stack === TemplateStack.SUPABASE_POLAR ||
      stack === TemplateStack.CLERK_CONVEX_POLAR
    );
  };

  const noPaymentInputStack =
    reactFramework === ReactFramework.VITE && isClerkConvexStack(templateStack);

  // Add state to track integration setup completion
  const [supabaseConnected, setSupabaseConnected] = useState<boolean>(false);

  const [numStripePlans, setNumStripePlans] = useState<number>(0);
  const [isLoadingStripePlans, setIsLoadingStripePlans] =
    useState<boolean>(false);
  const [stripePlansRefetchTrigger, setStripePlansRefetchTrigger] =
    useState<number>(0);

  useEffect(() => {
    if (noPaymentInputStack) {
      return;
    }

    if (!formValues.stripe?.secretKey || !hasStripePayment(templateStack)) {
      setNumStripePlans(0);
      return;
    }

    const checkStripePlans = async () => {
      setIsLoadingStripePlans(true);
      const stripe = new Stripe(formValues.stripe.secretKey);
      stripe.plans
        .list()
        .then((plans) => {
          setNumStripePlans(plans.data.length);
          setIsLoadingStripePlans(false);
        })
        .catch((e) => {
          setIsLoadingStripePlans(false);
          console.error(e);
        });
    };

    checkStripePlans();
  }, [
    templateStack,
    formValues.stripe.secretKey,
    stripePlansRefetchTrigger,
    noPaymentInputStack,
  ]);

  // Do the same thing for Polar
  const [numPolarProducts, setNumPolarProducts] = useState<number>(0);
  const [isLoadingPolarProducts, setIsLoadingPolarProducts] =
    useState<boolean>(false);
  const [polarProductsRefetchTrigger, setPolarProductsRefetchTrigger] =
    useState<number>(0);

  useEffect(() => {
    if (noPaymentInputStack) {
      return;
    }

    if (
      !formValues.polar?.accessToken ||
      !formValues.polar?.organizationId ||
      !hasPolarPayment(templateStack)
    ) {
      setNumPolarProducts(0);
      return;
    }

    const checkPolarProducts = async () => {
      setIsLoadingPolarProducts(true);
      const polar = new Polar({
        accessToken: formValues.polar.accessToken,
        server: "sandbox",
      });
      try {
        const { result } = await polar.products.list({
          organizationId: formValues.polar.organizationId,
          isArchived: false,
        });

        setNumPolarProducts(result?.items?.length || 0);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingPolarProducts(false);
      }
    };

    checkPolarProducts();
  }, [
    templateStack,
    formValues.polar.accessToken,
    formValues.polar.organizationId,
    polarProductsRefetchTrigger,
    noPaymentInputStack,
  ]);

  // Add a function to check for validation errors in form fields
  const hasValidationErrors = useCallback((): boolean => {
    // Check Clerk validation
    if (
      isClerkConvexStack(templateStack) &&
      formValues.clerk?.publishableKey &&
      !isValidClerkPublishableKey(formValues.clerk.publishableKey)
    ) {
      return true;
    }

    // Check Convex validation
    if (isClerkConvexStack(templateStack)) {
      if (
        formValues.convex?.deploymentUrl &&
        !isValidConvexDeploymentUrl(formValues.convex.deploymentUrl)
      ) {
        return true;
      }

      if (
        formValues.convex?.deploymentKey &&
        !isValidConvexDevKey(formValues.convex.deploymentKey)
      ) {
        return true;
      }
    }

    if (hasPolarPayment(templateStack) && !isClerkConvexStack(templateStack)) {
      if (
        formValues.polar?.accessToken &&
        !isValidPolarAccessToken(formValues.polar.accessToken)
      ) {
        return true;
      }

      if (
        formValues.polar?.organizationId &&
        !isValidPolarOrganizationId(formValues.polar.organizationId)
      ) {
        return true;
      }
    }

    if (hasStripePayment(templateStack) && !isClerkConvexStack(templateStack)) {
      if (
        formValues.stripe?.publicKey &&
        !isValidStripePublishableKey(formValues.stripe.publicKey)
      ) {
        return true;
      }

      if (
        formValues.stripe?.secretKey &&
        !isValidStripeSecretKey(formValues.stripe.secretKey)
      ) {
        return true;
      }

      if (
        formValues.stripe?.webhookSecret &&
        !isValidStripeWebhookSecret(formValues.stripe.webhookSecret)
      ) {
        return true;
      }
    }

    return false;
  }, [
    formValues.clerk.publishableKey,
    formValues.convex.deploymentKey,
    formValues.convex.deploymentUrl,
    formValues.polar.accessToken,
    formValues.polar.organizationId,
    formValues.stripe.publicKey,
    formValues.stripe.secretKey,
    formValues.stripe.webhookSecret,
    templateStack,
  ]);

  // Handle form field changes
  const handleFormChange = (
    integration: string,
    field: string,
    value: string,
  ) => {
    if (!onFormValuesChange) return;

    const updatedFormValues = {
      ...formValues,
      [integration]: {
        ...formValues[integration as keyof FormValues],
        [field]: value,
      },
    };

    onFormValuesChange(updatedFormValues);
  };

  // ===== Validation Functions =====
  const isValidConvexDeploymentUrl = (value: string) => {
    const convexUrlRegex = /^https:\/\/[a-z0-9-]+\.convex\.cloud\/?$/;
    return convexUrlRegex.test(value);
  };

  const isValidConvexDevKey = (value: string) => {
    const deployKeyRegex = /^dev:[a-z0-9-]+\|[A-Za-z0-9+/]+={1,2}$/;
    return deployKeyRegex.test(value);
  };

  const isValidClerkPublishableKey = (value: string) => {
    const clerkPublishableKeyRegex = /^pk_test_[A-Za-z0-9_]+$/;
    return clerkPublishableKeyRegex.test(value);
  };

  const isValidPolarAccessToken = (value: string) => {
    const polarKeyRegex = /^polar_oat_[A-Za-z0-9]+$/;
    return polarKeyRegex.test(value);
  };

  const isValidPolarOrganizationId = (value: string) => {
    const simpleUUIDRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return simpleUUIDRegex.test(value);
  };

  const isValidStripePublishableKey = (value: string): boolean => {
    const stripePublishableKeyRegex = /^pk_(test|live)_[A-Za-z0-9]+$/;
    return stripePublishableKeyRegex.test(value);
  };

  const isValidStripeSecretKey = (value: string): boolean => {
    const stripeSecretKeyRegex = /^sk_(test|live)_[A-Za-z0-9]+$/;
    return stripeSecretKeyRegex.test(value);
  };

  const isValidStripeWebhookSecret = (value: string): boolean => {
    const stripeWebhookSecretRegex = /^whsec_[A-Za-z0-9]+$/;
    return stripeWebhookSecretRegex.test(value);
  };

  const connectedProject = useMemo(() => {
    return connectedProjects.find(
      (_project) => _project.id === connectedProjectId,
    );
  }, [connectedProjectId, connectedProjects]);

  const supabasePaymentWebhookUrl = useMemo(() => {
    return isSupabaseStack(templateStack) &&
      supabaseConnected &&
      connectedProject
      ? `https://${connectedProject.id}.supabase.co/functions/v1/supabase-functions-payments-webhook`
      : undefined;
  }, [templateStack, supabaseConnected, connectedProject]);

  // ===== Render Functions =====

  // Function to check if all required fields for the selected stack are filled
  const integrationComplete = useMemo((): boolean => {
    // Check for validation errors in form fields
    if (hasValidationErrors()) {
      return false;
    }

    if (templateStack === null || templateStack === TemplateStack.BLANK) {
      return true; // No integrations needed
    }

    // Check Supabase connection
    if (isSupabaseStack(templateStack)) {
      if (!supabaseConnected) {
        return false;
      }
    }

    // Check Clerk+Convex
    if (isClerkConvexStack(templateStack)) {
      const clerkComplete = !!(
        formValues.clerk?.publishableKey &&
        formValues.clerk?.secretKey === "true"
      );
      const convexComplete = !!(
        formValues.convex?.deploymentUrl && formValues.convex.deploymentKey
      );

      if (!clerkComplete || !convexComplete) {
        return false;
      }
    }

    if (hasStripePayment(templateStack)) {
      const stripeComplete = noPaymentInputStack
        ? !!(
            formValues.stripe?.publicKey === "true" &&
            formValues.stripe?.secretKey === "true" &&
            formValues.stripe?.webhookSecret === "true" &&
            formValues.stripe?.verifyWebhookFields === "true"
          )
        : !!(
            formValues.stripe?.publicKey &&
            formValues.stripe?.secretKey &&
            formValues.stripe?.webhookSecret &&
            formValues.stripe?.verifyWebhookFields === "true" &&
            numStripePlans > 0
          );

      if (!stripeComplete) {
        return false;
      }
    }

    if (hasPolarPayment(templateStack)) {
      const polarComplete = noPaymentInputStack
        ? !!(
            formValues.polar?.accessToken === "true" &&
            formValues.polar?.webhookSecret === "true" &&
            formValues.polar?.organizationId === "true" &&
            formValues.polar?.verifyWebhookFields === "true"
          )
        : !!(
            formValues.polar?.accessToken &&
            formValues.polar?.webhookSecret &&
            formValues.polar?.organizationId &&
            formValues.polar?.verifyWebhookFields === "true" &&
            numPolarProducts > 0
          );

      if (!polarComplete) {
        return false;
      }
    }

    return true;
  }, [
    hasValidationErrors,
    templateStack,
    supabaseConnected,
    formValues.clerk?.publishableKey,
    formValues.clerk?.secretKey,
    formValues.convex?.deploymentUrl,
    formValues.convex.deploymentKey,
    formValues.stripe?.publicKey,
    formValues.stripe?.secretKey,
    formValues.stripe?.webhookSecret,
    formValues.stripe?.verifyWebhookFields,
    formValues.polar?.accessToken,
    formValues.polar?.webhookSecret,
    formValues.polar?.organizationId,
    formValues.polar?.verifyWebhookFields,
    noPaymentInputStack,
    numStripePlans,
    numPolarProducts,
  ]);

  // Step 2 - Integration setup

  // Main render
  return (
    <div className="flex flex-col">
      {/* Show message when no integrations are selected */}
      {(templateStack === null || templateStack === TemplateStack.BLANK) && (
        <div className="w-full max-w-3xl mb-4 border border-[#2A2A2D] p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-400">
              <path
                fill="currentColor"
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-4.42 3.58-8 8-8 4.42 0 8 3.58 8 8 0 4.42-3.58 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"
              />
            </svg>
            <h3 className="text-base font-medium">No Integrations Selected</h3>
          </div>
          <p className="text-gray-400 text-sm">
            You've chosen to proceed without any integrations. Your project will
            be created with just the{" "}
            {reactFramework === ReactFramework.VITE ? "Vite" : "Next.js"}{" "}
            framework.
          </p>
        </div>
      )}

      {/* Supabase Integration */}
      {isSupabaseStack(templateStack) && (
        <div className="w-full max-w-3xl mb-4 border border-[#2A2A2D] p-4 rounded-lg">
          <SupabaseConnect
            forProjectCreate={true}
            onConnectionStatusChange={(connected) => {
              setSupabaseConnected(connected);
            }}
          />
        </div>
      )}

      {/* Clerk + Convex Integration */}
      {isClerkConvexStack(templateStack) && (
        <>
          <IntegrationForm
            title="Clerk"
            icon="/images/clerk-auth.png"
            description="Enter your Clerk API keys for authentication."
            docsUrl="https://clerk.com/docs"
            fields={[
              {
                label: "Clerk Publishable Key",
                placeholder: "pk_test_...",
                onChange: (value) =>
                  handleFormChange("clerk", "publishableKey", value),
                validationFunction: isValidClerkPublishableKey,
                validationErrorMessage: "Invalid Clerk publishable key",
                value: formValues.clerk?.publishableKey || "",
              },
              {
                label: "Clerk Secret Key",
                placeholder: "sk_test_...",
                type: "checkbox",
                onChange: (value) =>
                  handleFormChange("clerk", "secretKey", value),
                checkboxLabel:
                  "Confirm that you have added your Clerk secret key in Convex.",
                value: formValues.clerk?.secretKey || "false",
              },
            ]}
            selectedTemplateStack={templateStack}
            reactFramework={reactFramework}
          />

          <IntegrationForm
            title="Convex"
            icon="/images/convex.png"
            description="Enter your Convex deployment URL and development key."
            docsUrl="https://docs.convex.dev"
            fields={[
              {
                label: "Convex Deployment URL",
                placeholder: "https://relaxed-puma-123.convex.cloud",
                onChange: (value) =>
                  handleFormChange("convex", "deploymentUrl", value),
                validationFunction: isValidConvexDeploymentUrl,
                validationErrorMessage: "Invalid Convex deployment URL",
                value: formValues.convex?.deploymentUrl || "",
              },
              {
                label: "Convex Deploy Key",
                placeholder: "dev_key_...",
                type: "password",
                onChange: (value) =>
                  handleFormChange("convex", "deploymentKey", value),
                validationFunction: isValidConvexDevKey,
                validationErrorMessage: "Invalid Convex deploy key",
                value: formValues.convex?.deploymentKey || "",
              },
            ]}
            selectedTemplateStack={templateStack}
            reactFramework={reactFramework}
          />
        </>
      )}

      {/* Payment Providers */}
      {hasStripePayment(templateStack) && (
        <IntegrationForm
          title="Stripe"
          icon="/images/stripe-icon.svg"
          description={
            reactFramework === ReactFramework.VITE &&
            templateStack === TemplateStack.CLERK_CONVEX_STRIPE
              ? "Your Stripe API keys must be stored in Convex."
              : "Accept payments and manage subscriptions with Stripe."
          }
          docsUrl="https://stripe.com/docs"
          selectedTemplateStack={templateStack}
          reactFramework={reactFramework}
          isInfoOnly={isSupabaseStack(templateStack) && !supabaseConnected}
          infoMessage={
            reactFramework === ReactFramework.VITE &&
            templateStack === TemplateStack.CLERK_CONVEX_STRIPE
              ? `Your Stripe API keys will be securely stored in Convex when you deploy your application.`
              : isSupabaseStack(templateStack) && !supabaseConnected
                ? "Connect Supabase to set up Stripe"
                : undefined
          }
          fields={
            !(
              reactFramework === ReactFramework.VITE &&
              templateStack === TemplateStack.CLERK_CONVEX_STRIPE
            )
              ? [
                  {
                    label: "Stripe Publishable Key",
                    placeholder: "pk_test_...",
                    onChange: (value) =>
                      handleFormChange("stripe", "publicKey", value),
                    validationFunction: isValidStripePublishableKey,
                    validationErrorMessage: "Invalid Stripe publishable key",
                    value: formValues.stripe?.publicKey || "",
                  },
                  {
                    label: "Stripe Secret Key",
                    placeholder: "sk_test_...",
                    type: "password",
                    onChange: (value) =>
                      handleFormChange("stripe", "secretKey", value),
                    validationFunction: isValidStripeSecretKey,
                    validationErrorMessage: "Invalid Stripe secret key",
                    value: formValues.stripe?.secretKey || "",
                  },
                  {
                    label: "Stripe Webhook Secret",
                    placeholder: "whsec_...",
                    type: "password",
                    onChange: (value) =>
                      handleFormChange("stripe", "webhookSecret", value),
                    validationFunction: isValidStripeWebhookSecret,
                    validationErrorMessage: "Invalid Stripe webhook secret",
                    value: formValues.stripe?.webhookSecret || "",
                  },
                  {
                    label: "Verify Webhook Fields",
                    type: "checkbox",
                    placeholder: "_",
                    onChange: (value) =>
                      handleFormChange("stripe", "verifyWebhookFields", value),
                    checkboxLabel: (
                      <p className="text-white text-sm">
                        I confirm that I have selected{" "}
                        <span className="underline">all events</span> for my
                        Stripe webhook.
                      </p>
                    ),
                    value: formValues.stripe?.verifyWebhookFields || "",
                  },
                ]
              : [
                  {
                    label: "Stripe Publishable Key",
                    placeholder: "pk_test_...",
                    onChange: (value) =>
                      handleFormChange("stripe", "publicKey", value),
                    type: "checkbox",
                    checkboxLabel: (
                      <p className="text-white text-sm">
                        I confirm that I have added my{" "}
                        <span className="underline">
                          Stripe publishable key
                        </span>{" "}
                        in Convex.
                      </p>
                    ),
                    value: formValues.stripe?.publicKey || "",
                  },
                  {
                    label: "Stripe Secret Key",
                    placeholder: "sk_test_...",
                    type: "checkbox",
                    onChange: (value) =>
                      handleFormChange("stripe", "secretKey", value),
                    checkboxLabel: (
                      <p className="text-white text-sm">
                        I confirm that I have added my{" "}
                        <span className="underline">Stripe secret key</span> in
                        Convex.
                      </p>
                    ),
                    value: formValues.stripe?.secretKey || "",
                  },
                  {
                    label: "Stripe Webhook Secret",
                    placeholder: "whsec_...",
                    type: "checkbox",
                    onChange: (value) =>
                      handleFormChange("stripe", "webhookSecret", value),
                    checkboxLabel: (
                      <p className="text-white text-sm">
                        I confirm that I have added my{" "}
                        <span className="underline">Stripe webhook secret</span>{" "}
                        in Convex.
                      </p>
                    ),
                    value: formValues.stripe?.webhookSecret || "",
                  },
                  {
                    label: "Verify Webhook Fields",
                    type: "checkbox",
                    placeholder: "_",
                    onChange: (value) =>
                      handleFormChange("stripe", "verifyWebhookFields", value),
                    checkboxLabel: (
                      <p className="text-white text-sm">
                        I confirm that I have selected{" "}
                        <span className="underline">all events</span> for my
                        Stripe webhook.
                      </p>
                    ),
                    value: formValues.stripe?.verifyWebhookFields || "",
                  },
                ]
          }
          footer={
            <>
              <div
                className={cn(
                  "mt-3 bg-[#1F1F22] p-3 rounded-md border border-[#2A2A2D]",
                  !supabasePaymentWebhookUrl && "hidden",
                )}
              >
                <p className="text-xs text-gray-300 mb-2">
                  Copy this URL for the Stripe webhook:
                </p>
                <div
                  className="flex items-center justify-between bg-[#2A2A2D] p-2 rounded cursor-pointer hover:bg-[#333338]"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      // @ts-expect-error TS2345
                      supabasePaymentWebhookUrl,
                    );
                    toast.success("Copied to clipboard");
                  }}
                >
                  <span className="text-xs text-white font-mono">
                    {supabasePaymentWebhookUrl}
                  </span>
                  <Copy className="h-4 w-4 text-gray-400" />
                </div>
              </div>
              <div
                className={cn(
                  "mt-3 bg-[#1F1F22] p-3 rounded-md border border-[#2A2A2D]",
                  (!formValues.stripe?.secretKey ||
                    noPaymentInputStack ||
                    (isSupabaseStack(templateStack) && !supabaseConnected)) &&
                    "hidden",
                )}
              >
                <div className="flex justify-between items-center">
                  <p
                    className={cn(
                      "text-xs text-gray-300",
                      numStripePlans === 0 && "text-red-500",
                    )}
                  >
                    {numStripePlans > 0
                      ? `You have ${numStripePlans} Stripe plan(s) set up in this project.`
                      : "You have no Stripe plans set up in this project."}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-6 px-2"
                    onClick={() => {
                      setStripePlansRefetchTrigger((prev) => prev + 1);
                      toast.success("Refreshed Stripe plans");
                    }}
                    disabled={isLoadingStripePlans}
                  >
                    {isLoadingStripePlans ? "Loading..." : "Refresh"}
                  </Button>
                </div>
              </div>
            </>
          }
        />
      )}

      {hasPolarPayment(templateStack) && (
        <IntegrationForm
          title="Polar"
          icon="/images/polar-sh_dark.svg"
          description={
            reactFramework === ReactFramework.VITE &&
            templateStack === TemplateStack.CLERK_CONVEX_POLAR
              ? "Your Polar API keys must be stored in Convex."
              : "Accept payments and manage subscriptions with Polar."
          }
          docsUrl="https://polar.sh/docs"
          selectedTemplateStack={templateStack}
          reactFramework={reactFramework}
          isInfoOnly={isSupabaseStack(templateStack) && !supabaseConnected}
          infoMessage={
            reactFramework === ReactFramework.VITE &&
            templateStack === TemplateStack.CLERK_CONVEX_POLAR
              ? `Your Polar API keys will be securely stored in Convex when you deploy your application. You'll need to enter them later.`
              : isSupabaseStack(templateStack) && !supabaseConnected
                ? "Connect Supabase to set up Polar"
                : undefined
          }
          fields={
            !(
              reactFramework === ReactFramework.VITE &&
              templateStack === TemplateStack.CLERK_CONVEX_POLAR
            )
              ? [
                  {
                    label: "Polar Access Token",
                    placeholder: "polar_oat_...",
                    type: "password",
                    onChange: (value) =>
                      handleFormChange("polar", "accessToken", value),
                    validationFunction: isValidPolarAccessToken,
                    validationErrorMessage: "Invalid Polar access token",
                    value: formValues.polar?.accessToken || "",
                  },
                  {
                    label: "Polar Organization ID",
                    placeholder: "58045f0b-fa90-...",
                    onChange: (value) =>
                      handleFormChange("polar", "organizationId", value),
                    validationFunction: isValidPolarOrganizationId,
                    validationErrorMessage: "Invalid Polar organization ID",
                    value: formValues.polar?.organizationId || "",
                  },
                  {
                    label: "Polar Webhook Secret",
                    placeholder: "a1b2c3...",
                    type: "password",
                    onChange: (value) =>
                      handleFormChange("polar", "webhookSecret", value),
                    value: formValues.polar?.webhookSecret || "",
                  },
                  {
                    label: "Verify Webhook Fields",
                    type: "checkbox",
                    placeholder: "_",
                    onChange: (value) =>
                      handleFormChange("polar", "verifyWebhookFields", value),
                    checkboxLabel: (
                      <p className="text-white text-sm">
                        I confirm that I have selected{" "}
                        <span className="underline">all events</span> for my
                        Polar webhook.
                      </p>
                    ),
                    value: formValues.polar?.verifyWebhookFields || "",
                  },
                ]
              : [
                  {
                    label: "Polar Access Token",
                    placeholder: "polar_sk_...",
                    type: "checkbox",
                    onChange: (value) =>
                      handleFormChange("polar", "accessToken", value),
                    checkboxLabel: (
                      <p className="text-white text-sm">
                        I confirm that I have added my{" "}
                        <span className="underline">Polar access token</span> in
                        Convex.
                      </p>
                    ),
                    value: formValues.polar?.accessToken || "",
                  },
                  {
                    label: "Polar Organization ID",
                    placeholder: "polar_org_...",
                    type: "checkbox",
                    onChange: (value) =>
                      handleFormChange("polar", "organizationId", value),
                    checkboxLabel: (
                      <p className="text-white text-sm">
                        I confirm that I have added my{" "}
                        <span className="underline">Polar organization ID</span>{" "}
                        in Convex.
                      </p>
                    ),
                    value: formValues.polar?.organizationId || "",
                  },
                  {
                    label: "Polar Webhook Secret",
                    placeholder: "whsec_...",
                    type: "checkbox",
                    onChange: (value) =>
                      handleFormChange("polar", "webhookSecret", value),
                    checkboxLabel: (
                      <p className="text-white text-sm">
                        I confirm that I have added my{" "}
                        <span className="underline">Polar webhook secret</span>{" "}
                        in Convex.
                      </p>
                    ),
                    value: formValues.polar?.webhookSecret || "",
                  },
                  {
                    label: "Verify Webhook Fields",
                    type: "checkbox",
                    placeholder: "_",
                    onChange: (value) =>
                      handleFormChange("polar", "verifyWebhookFields", value),
                    checkboxLabel: (
                      <p className="text-white text-sm">
                        I confirm that I have selected{" "}
                        <span className="underline">all events</span> for my
                        Polar webhook.
                      </p>
                    ),
                    value: formValues.polar?.verifyWebhookFields || "",
                  },
                ]
          }
          footer={
            <>
              {supabasePaymentWebhookUrl ? (
                <div className="mt-3 bg-[#1F1F22] p-3 rounded-md border border-[#2A2A2D]">
                  <p className="text-xs text-gray-300 mb-2">
                    Copy this URL for the Polar webhook:
                  </p>
                  <div
                    className="flex items-center justify-between bg-[#2A2A2D] p-2 rounded cursor-pointer hover:bg-[#333338]"
                    onClick={() => {
                      navigator.clipboard.writeText(supabasePaymentWebhookUrl);
                      toast.success("Copied to clipboard");
                    }}
                  >
                    <span className="text-xs text-white font-mono">
                      {supabasePaymentWebhookUrl}
                    </span>
                    <Copy className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              ) : null}
              <div
                className={cn(
                  "mt-3 bg-[#1F1F22] p-3 rounded-md border border-[#2A2A2D]",
                  (!formValues.polar?.accessToken ||
                    !formValues.polar?.organizationId ||
                    noPaymentInputStack ||
                    (isSupabaseStack(templateStack) && !supabaseConnected)) &&
                    "hidden",
                )}
              >
                <div className="flex justify-between items-center">
                  <p
                    className={cn(
                      "text-xs text-gray-300",
                      numPolarProducts === 0 && "text-red-500",
                    )}
                  >
                    {numPolarProducts > 0 ? (
                      `You have ${numPolarProducts} Polar product(s) set up in this project.`
                    ) : (
                      <>
                        You have no Polar products set up in this project.
                        Please make sure your keys are from{" "}
                        <a
                          href="https://sandbox.polar.sh"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline"
                        >
                          sandbox.polar.sh
                        </a>{" "}
                        and have created a product and added it to your project.
                      </>
                    )}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-6 px-2"
                    onClick={() => {
                      setPolarProductsRefetchTrigger((prev) => prev + 1);
                      toast.success("Refreshed Polar products");
                    }}
                    disabled={isLoadingPolarProducts}
                  >
                    {isLoadingPolarProducts ? "Loading..." : "Refresh"}
                  </Button>
                </div>
              </div>
            </>
          }
        />
      )}

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onBack}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            if (integrationComplete) {
              toast.success("Integrations setup complete");
              // @ts-expect-error TS2345
              onComplete(templateStack);
            } else {
              // If integration is not complete, go back
              onBack();
            }
          }}
          disabled={!integrationComplete}
        >
          {saveActionText}
        </Button>
      </div>
    </div>
  );
};
